import React, { useEffect, useRef, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { getUploadErrorMessage, uploadImageToImgbb } from "../utils/uploadImageToImgbb";
import { AGRICULTURAL_CATEGORIES } from "../utils/agriculturalCategories";
import {
  calculateExpiration,
  getOptimalStorageCondition,
  getAvailableStatesForCategory,
  PRODUCT_STATES,
  STORAGE_CONDITIONS,
} from "../utils/expirationPredictor";
import {
  ChevronLeft,
  Trash2,
  Star,
  ChevronRight,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader,
  Leaf,
  X,
  Tag,
  Clock,
  ChevronDown,
} from "lucide-react";
import LocationPicker from "../components/LocationPicker";
import { useToast } from "../contexts/ToastContext";
import { Button, Input, Textarea } from "../components/ui";

const makeId = () => `img_${Date.now()}_${Math.floor(Math.random() * 10000)}`;

// Resize image on-device to save bandwidth (returns a File)
async function resizeImageFile(file, maxWidth = 1600, quality = 0.8) {
  if (!file) return file;
  // if file is already small-ish, skip heavy work
  try {
    const img = await new Promise((res, rej) => {
      const r = new FileReader();
      r.onerror = rej;
      r.onload = () => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = rej;
        i.src = r.result;
      };
      r.readAsDataURL(file);
    });

    let { width, height } = img;
    if (width <= maxWidth) {
      // re-encode to jpeg to reduce size if needed
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
      return new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
    }

    const ratio = maxWidth / width;
    const newW = Math.round(width * ratio);
    const newH = Math.round(height * ratio);
    const canvas = document.createElement("canvas");
    canvas.width = newW;
    canvas.height = newH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, newW, newH);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    return new File([blob], file.name.replace(/\.[^/.]+$/, ".jpg"), { type: "image/jpeg" });
  } catch (err) {
    // Image resize error silently handled
    return file;
  }
}

export default function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { user, loading: authLoading } = useAuth();
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    title: "",
    price: "",
    quantity: "",
    category: "",
    productState: "",
    desc: "",
    location: null,
  });

  const [storageCondition, setStorageCondition] = useState(STORAGE_CONDITIONS.AMBIENT);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryDropdownRef = useRef(null);

  // images: array of { id, src (url or objectURL), file?, isNew, isUploading, uploadedUrl }
  const [images, setImages] = useState([]);
  const [coverId, setCoverId] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // revoke any object URLs on unmount
      images.forEach((it) => {
        if (it.isNew && it.src && it.src.startsWith("blob:")) URL.revokeObjectURL(it.src);
      });
      mountedRef.current = false;
    };
    
  }, []);

  useEffect(() => {
    if (authLoading) return;
    async function load() {
      try {
        setLoading(true);
        const ref = doc(db, "products", id);
        const snap = await getDoc(ref);
        if (!snap.exists()) throw new Error("Product not found");
        const data = snap.data();

        const uid = user?.uid;
        const ownerId = data.poster?.userId || data.userId;
        if (!uid || ownerId !== uid) {
          setError("You are not allowed to edit this product.");
          setLoading(false);
          return;
        }

        setForm((prev) => ({
          ...prev,
          title: data.title || "",
          price: data.price ?? "",
          quantity: data.quantity ?? "",
          category: data.category || "",
          productState: data.productState || PRODUCT_STATES.FRESH,
          desc: data.desc || "",
          location: data.location || null,
        }));

        const condition = data.storageCondition || STORAGE_CONDITIONS.AMBIENT;
        setStorageCondition(condition);

        const existing = (data.images || []).map((url, i) => ({
          id: makeId(),
          src: url,
          file: null,
          isNew: false,
          isUploading: false,
          uploadedUrl: url,
        }));

        setImages(existing);
        setCoverId(existing[0]?.id ?? null);
      } catch (err) {
        // Load error silently handled
        setError("We could not load this product right now. Please try again.");
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
  }, [id, user, authLoading]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  }

  function handleLocationChange(loc) {
    setForm((p) => ({ ...p, location: loc }));
  }

  function handleFilesSelected(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newItems = files.map((f) => {
      const id = makeId();
      return {
        id,
        src: URL.createObjectURL(f),
        file: f,
        isNew: true,
        isUploading: false,
        uploadedUrl: null,
      };
    });
    setImages((prev) => {
      const merged = [...prev, ...newItems];
      if (!coverId && merged.length) setCoverId(merged[0].id);
      return merged;
    });
    // reset input so same file can be re-selected if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeImageAt(index) {
    setImages((prev) => {
      const removed = prev[index];
      if (removed?.isNew && removed.src && removed.src.startsWith("blob:")) {
        try { URL.revokeObjectURL(removed.src); } catch (e) {}
      }
      const next = prev.filter((_, i) => i !== index);
      if (coverId === removed?.id && next.length) setCoverId(next[0].id);
      if (next.length === 0) setCoverId(null);
      return next;
    });
  }

  function moveImage(fromIndex, toIndex) {
    setImages((prev) => {
      const copy = [...prev];
      const [item] = copy.splice(fromIndex, 1);
      copy.splice(toIndex, 0, item);
      return copy;
    });
  }

  // drag handlers
  function onDragStart(e, index) {
    e.dataTransfer.setData("text/plain", String(index));
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragOver(e) {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }
  function onDrop(e, index) {
    e.preventDefault();
    const from = Number(e.dataTransfer.getData("text/plain"));
    if (Number.isFinite(from)) moveImage(from, index);
  }

  function pickCover(id) {
    setCoverId(id);
    // move cover to first position visually (not persisted yet)
    setImages((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx <= 0) return prev;
      const copy = [...prev];
      const [it] = copy.splice(idx, 1);
      copy.unshift(it);
      return copy;
    });
  }

  // Available product states for selected category
  const productStateOptions = useMemo(() => {
    if (!form.category) return [PRODUCT_STATES.FRESH];
    return getAvailableStatesForCategory(form.category);
  }, [form.category]);

  // Price helper text
  const priceHelper = useMemo(() => {
    const n = Number(form.price);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US") + " RWF";
  }, [form.price]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (!form.title?.trim()) return setError("Title is required");
    if (!images.length) return setError("Please add at least one image");

    // prepare ordered images: ensure cover is first
    if (coverId) {
      setImages((prev) => {
        const idx = prev.findIndex((p) => p.id === coverId);
        if (idx <= 0) return prev;
        const copy = [...prev];
        const [it] = copy.splice(idx, 1);
        copy.unshift(it);
        return copy;
      });
    }

    setSubmitting(true);

    try {
      // Upload new images sequentially to make progress predictable and avoid burst
      const updatedItems = [...images];

      for (let i = 0; i < updatedItems.length; i++) {
        const item = updatedItems[i];
        if (item.isNew && !item.uploadedUrl) {
          updatedItems[i] = { ...item, isUploading: true };
          setImages([...updatedItems]);

          // try to resize first
          const toUploadFile = await resizeImageFile(item.file, 1600, 0.78);
          const uploadedUrl = await uploadImageToImgbb(toUploadFile, { target: "product" });

          updatedItems[i] = {
            ...updatedItems[i],
            isUploading: false,
            uploadedUrl,
            src: uploadedUrl, // replace preview with final url
          };
          setImages([...updatedItems]);
        }
      }

      // final image URLs (in order)
      const finalImages = updatedItems.map((it) => it.uploadedUrl || it.src).filter(Boolean);

      // write to firestore
      const updates = {
        title: form.title,
        price: form.price === "" ? null : Number(form.price),
        pricePerKg: form.price === "" ? null : Number(form.price),
        quantity: form.quantity === "" ? null : form.quantity,
        category: form.category || null,
        productState: form.productState || PRODUCT_STATES.FRESH,
        storageCondition: storageCondition,
        desc: form.desc || "",
        location: form.location || null,
        images: finalImages,
        coverUrl: finalImages[0] || null,
        updatedAt: serverTimestamp(),
        titleLower: (form.title || "").toLowerCase(),
        descLower: (form.desc || "").toLowerCase(),
      };

      await updateDoc(doc(db, "products", id), updates);

      toast.success("Your product has been updated successfully!");
      navigate(`/product/${id}`);
    } catch (err) {
      setError(getUploadErrorMessage(err) || "Failed to update product");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }

  if (loading) return (
    <div className="flex justify-center items-center h-screen bg-white dark:bg-neutral-900">
      <div className="flex flex-col items-center gap-4">
        <span className="orrith-loader orrith-loader-md orrith-loader-primary" />
        <p className="text-gray-600 dark:text-gray-400 font-medium">Loading product…</p>
      </div>
    </div>
  );

  if (error && !form.title) return (
    <div className="min-h-screen bg-white dark:bg-neutral-900 flex items-center justify-center p-4">
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 max-w-md text-center">
        <AlertCircle className="w-8 h-8 text-red-600 mx-auto mb-3" />
        <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-neutral-900 dark:to-neutral-950">
      {/* Mobile header with close button (consistent with ProductForm) */}
      <div className="flex items-center justify-between sm:hidden -mx-4 px-4 py-3 border-b border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-800/50 sticky top-0 z-10">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-700 text-gray-700 dark:text-gray-300 transition-colors"
          aria-label="Cancel"
        >
          <X size={20} />
        </button>
        <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Edit Product
        </h1>
        <div className="w-8" />
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-6 pb-20 space-y-6">
        {/* Header (desktop only) */}
        <div className="hidden sm:flex items-center gap-3 mb-6">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-white dark:bg-neutral-800 shadow-sm">
            <Leaf className="w-5 h-5 text-green-600" />
          </span>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">
              Edit Product
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Update your harvest with the community
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="flex gap-3 p-3 sm:p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">{error}</p>
            </div>
            <button
              type="button"
              onClick={() => setError("")}
              className="text-red-600 dark:text-red-400 hover:opacity-70"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Photos Section */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-green-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Photos
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {images.length}/3 uploaded
                </p>
              </div>
            </div>
            {images.length > 0 && (
              <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-full">
                <p className="text-xs font-semibold text-green-700 dark:text-green-400">
                  {images.length === 3 ? "Complete" : `${3 - images.length} more`}
                </p>
              </div>
            )}
          </div>

          {/* Upload Area */}
          {images.length === 0 ? (
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center bg-gray-50 dark:bg-neutral-900/30 hover:border-green-400 dark:hover:border-green-600 transition cursor-pointer">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFilesSelected}
                className="hidden"
              />
              <Upload className="w-8 h-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Add up to 3 photos
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Drag and drop or tap to upload
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition"
              >
                Select Photos
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                {images.map((it, i) => (
                  <div
                    key={it.id}
                    draggable
                    onDragStart={(e) => onDragStart(e, i)}
                    onDragOver={onDragOver}
                    onDrop={(e) => onDrop(e, i)}
                    className="relative group"
                  >
                    <div className="aspect-square overflow-hidden rounded-lg bg-gray-100 dark:bg-neutral-700">
                      <img
                        src={it.src}
                        alt={`Product ${i + 1}`}
                        className={`w-full h-full object-cover transition ${
                          i === 0
                            ? "ring-2 ring-green-600 ring-inset"
                            : "ring-1 ring-gray-200 dark:ring-gray-700"
                        }`}
                        onError={(e) => (e.currentTarget.src = "/placeholder-product.png")}
                      />

                      {/* Cover Badge */}
                      {i === 0 && (
                        <span className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded-full text-xs font-medium">
                          Cover
                        </span>
                      )}

                      {/* Uploading Overlay */}
                      {it.isUploading && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <span className="orrith-loader orrith-loader-sm orrith-loader-on-dark" />
                        </div>
                      )}
                    </div>

                    {/* Controls - Hover */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition rounded-lg flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        title="Set as cover"
                        onClick={() => pickCover(it.id)}
                        className="p-2 bg-white dark:bg-neutral-700 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-600 transition"
                      >
                        <Star
                          className={`w-5 h-5 ${
                            it.id === coverId
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-gray-400"
                          }`}
                        />
                      </button>

                      {i > 0 && (
                        <button
                          type="button"
                          onClick={() => {
                            const idx = images.findIndex((x) => x.id === it.id);
                            if (idx > 0) moveImage(idx, idx - 1);
                          }}
                          className="p-2 bg-white dark:bg-neutral-700 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-600 transition"
                        >
                          <ChevronLeft className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                        </button>
                      )}

                      {i < images.length - 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const idx = images.findIndex((x) => x.id === it.id);
                            if (idx < images.length - 1) moveImage(idx, idx + 1);
                          }}
                          className="p-2 bg-white dark:bg-neutral-700 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-600 transition"
                        >
                          <ChevronRight className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => {
                          const idx = images.findIndex((x) => x.id === it.id);
                          removeImageAt(idx);
                        }}
                        className="p-2 bg-red-500 hover:bg-red-600 rounded-full transition"
                      >
                        <Trash2 className="w-5 h-5 text-white" />
                      </button>
                    </div>
                  </div>
                ))}

                {images.length > 0 && (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center hover:border-green-400 dark:hover:border-green-600 transition"
                  >
                    <Upload className="w-6 h-6 text-gray-400" />
                  </button>
                )}
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFilesSelected}
            className="hidden"
          />
        </div>

        {/* Product Details */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
          <div className="flex items-center gap-2 mb-2">
            <Tag className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Product Details
            </h2>
          </div>

          {/* Product Name */}
          <div className="md:grid md:grid-cols-12 md:items-center md:gap-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 md:mb-0 md:col-span-3">
              Product Name
            </label>
            <div className="md:col-span-9">
              <Input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                required
                placeholder={form.category ? "Enter product name..." : "Select category first"}
                disabled={!form.category}
              />
            </div>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <div className="relative" ref={categoryDropdownRef}>
              <button
                type="button"
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500 flex items-center justify-between text-left"
              >
                <span>{form.category || "Select a category..."}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showCategoryDropdown ? "rotate-180" : ""}`} />
              </button>
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white dark:bg-neutral-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-20">
                  {AGRICULTURAL_CATEGORIES.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        setForm((prev) => ({ ...prev, category: cat.name, productState: PRODUCT_STATES.FRESH }));
                        setShowCategoryDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-green-50 dark:hover:bg-green-900/20 text-sm text-gray-900 dark:text-gray-100 border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product State & Storage Condition */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Product State
              </label>
              <select
                name="productState"
                value={form.productState}
                onChange={handleChange}
                required
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500"
              >
                {productStateOptions.length > 0 ? (
                  productStateOptions.map((state) => (
                    <option key={state} value={state}>
                      {state.charAt(0).toUpperCase() + state.slice(1)}
                    </option>
                  ))
                ) : (
                  <option value={PRODUCT_STATES.FRESH}>Fresh</option>
                )}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Available for {form.category || "selected category"}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Storage Condition
              </label>
              <select
                value={storageCondition}
                onChange={(e) => setStorageCondition(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-neutral-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-green-600 dark:focus:ring-green-500"
              >
                <option value={STORAGE_CONDITIONS.AMBIENT}>Ambient</option>
                <option value={STORAGE_CONDITIONS.REFRIGERATED}>Refrigerated</option>
                <option value={STORAGE_CONDITIONS.FROZEN}>Frozen</option>
                <option value={STORAGE_CONDITIONS.CONTROLLED_ATMOSPHERE}>Controlled Atmosphere</option>
                <option value={STORAGE_CONDITIONS.VACUUM_SEALED}>Vacuum Sealed</option>
                <option value={STORAGE_CONDITIONS.MODIFIED_ATMOSPHERE}>Modified Atmosphere</option>
                <option value={STORAGE_CONDITIONS.DRY_STORAGE}>Dry Storage</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Suggested: <span className="font-semibold text-green-600 dark:text-green-400">{storageCondition}</span>
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Description
            </label>
            <Textarea
              name="desc"
              value={form.desc}
              onChange={handleChange}
              placeholder="Add details about the product..."
              rows={4}
            />
          </div>
        </div>

        {/* Pricing & Quantity */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Tag className="w-5 h-5 text-green-600" />
            Pricing & Quantity
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Price (RWF)
              </label>
              <Input
                type="number"
                name="price"
                value={form.price}
                onChange={handleChange}
                required
                min="0"
                placeholder="0"
              />
              {priceHelper && (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{priceHelper}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Quantity (kg)
              </label>
              <Input
                type="number"
                name="quantity"
                value={form.quantity}
                onChange={handleChange}
                min="0"
                placeholder="Amount in kilograms"
              />
            </div>
          </div>
        </div>

        {/* Location */}
        <div className="bg-white dark:bg-neutral-800/80 rounded-xl p-4 sm:p-6 shadow-md dark:shadow-lg border border-gray-200 dark:border-neutral-700/50 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Location</h2>
          <LocationPicker value={form.location} onChange={handleLocationChange} />
        </div>

        {/* Action Buttons */}
        <div className="sticky bottom-0 bg-white dark:bg-neutral-800/95 backdrop-blur border-t border-gray-200 dark:border-neutral-700 p-4 -mx-4 flex gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(-1)}
            className="flex-1 px-6 py-3 text-sm"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={submitting}
            className="flex-1 px-6 py-3 text-sm"
          >
            {submitting ? (
              <>
                <span className="orrith-loader orrith-loader-sm orrith-loader-on-dark" />
                Updating…
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Save
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}